import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pose } from '@mediapipe/pose';
import { Camera } from '@mediapipe/camera_utils';
import { drawConnectors, drawLandmarks } from '@mediapipe/drawing_utils';
import { POSE_CONNECTIONS } from '@mediapipe/pose';
import { useAssessment } from '../../context/AssessmentContext';
import { Card, Button, ProgressBar } from '../common';

/**
 * Physical Tests Module - 7 exercises, choose 4
 * Each with unique metrics and audio instructions
 */
const PhysicalTestsModule = () => {
  const navigate = useNavigate();
  const { savePhysicalTest, completeAssessment, currentAssessment } = useAssessment();
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const poseRef = useRef(null);
  const cameraRef = useRef(null);
  const audioRef = useRef(null);
  const repStateRef = useRef('up');
  
  const [completedTests, setCompletedTests] = useState([]);
  const [currentTest, setCurrentTest] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [metrics, setMetrics] = useState({});
  const [showInstructions, setShowInstructions] = useState(false);
  const [testStartTime, setTestStartTime] = useState(null);
  const timerIntervalRef = useRef(null);

  // 7 Available Tests
  const availableTests = [
    {
      id: 'squat',
      name: 'Squats',
      icon: '🏋️',
      description: 'Lower body strength',
      target: 5,
      type: 'reps',
      metrics: ['kneeAngle', 'hipHeight']
    },
    {
      id: 'lunge',
      name: 'Lunges',
      icon: '🦵',
      description: 'Leg strength & balance',
      target: 5,
      type: 'reps',
      metrics: ['frontKneeAngle', 'backKneeAngle', 'balance']
    },
    {
      id: 'tpose',
      name: 'T-Pose Balance',
      icon: '🤸',
      description: 'Shoulder stability & balance',
      target: 30,
      type: 'time',
      metrics: ['armAngle', 'shoulderAlignment', 'legTouches', 'holdTime']
    },
    {
      id: 'highknee',
      name: 'High Knee March',
      icon: '🏃',
      description: 'Hip flexor strength',
      target: 10,
      type: 'reps',
      metrics: ['kneeHeight', 'hipAngle']
    },
    {
      id: 'plank',
      name: 'Plank Hold',
      icon: '🧘',
      description: 'Core strength',
      target: 30,
      type: 'time',
      metrics: ['backAngle', 'hipAlignment', 'holdTime']
    },
    {
      id: 'pushup',
      name: 'Push-ups',
      icon: '💪',
      description: 'Upper body strength',
      target: 5,
      type: 'reps',
      metrics: ['elbowAngle', 'bodyAlignment', 'depth']
    },
    {
      id: 'forwardbend',
      name: 'Forward Bend',
      icon: '🙇',
      description: 'Flexibility',
      target: 5,
      type: 'reps',
      metrics: ['hipAngle', 'kneeStraightness', 'reachDistance']
    }
  ];

  const audioInstructions = {
    intro: (testName, target, type) => 
      `${testName}. Complete ${target} ${type === 'reps' ? 'repetitions' : 'seconds'}. Click start when ready.`,
    complete: 'Test complete! Click your next test.',
    allComplete: 'All tests complete! Proceed to reports.'
  };

  useEffect(() => {
    return () => {
      if (cameraRef.current) {
        cameraRef.current.stop();
      }
    };
  }, []);

  const speakInstruction = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1;
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleSelectTest = (test) => {
    if (completedTests.some(t => t.testId === test.id)) {
      alert('You have already completed this test!');
      return;
    }
    
    setCurrentTest(test);
    setShowInstructions(true);
    setMetrics({ reps: 0, time: 0, ...initializeMetrics(test) });
    
    // Speak instructions
    speakInstruction(audioInstructions.intro(test.name, test.target, test.type));
  };

  const initializeMetrics = (test) => {
    const metricsObj = {};
    test.metrics.forEach(metric => {
      metricsObj[metric] = 0;
    });
    return metricsObj;
  };

  const handleStartTest = async () => {
    setShowInstructions(false);
    setIsTracking(true);
    repStateRef.current = 'up';
    
    // Start timer for ALL tests
    setTestStartTime(Date.now());
    startUniversalTimer();
    
    try {
      await initializeMediaPipe();
      if (cameraRef.current) {
        await cameraRef.current.start();
      }
      
      // For time-based tests (plank, t-pose), also check for auto-complete
      if (currentTest.type === 'time') {
        startTimeBasedTest();
      }
    } catch (error) {
      console.error('Error starting test:', error);
      alert('Camera error. Please check permissions.');
      setIsTracking(false);
    }
  };

  const startUniversalTimer = () => {
    const startTime = Date.now();
    timerIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setMetrics(prev => ({ ...prev, elapsedTime: elapsed }));
    }, 100);
  };

  const startTimeBasedTest = () => {
    const startTime = Date.now();
    const timeCheckInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setMetrics(prev => ({ ...prev, time: elapsed, holdTime: elapsed }));
      
      if (elapsed >= currentTest.target) {
        clearInterval(timeCheckInterval);
        handleTestComplete();
      }
    }, 100);
  };

  const initializeMediaPipe = async () => {
    console.log('Initializing MediaPipe...');
    
    const pose = new Pose({
      locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`
    });

    pose.setOptions({
      modelComplexity: 1,
      smoothLandmarks: true,
      enableSegmentation: false,
      smoothSegmentation: false,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
      upperBodyOnly: false
    });

    pose.onResults(onPoseResults);
    await pose.initialize();
    poseRef.current = pose;
    console.log('MediaPipe initialized');

    if (videoRef.current) {
      const camera = new Camera(videoRef.current, {
        onFrame: async () => {
          if (poseRef.current) {
            await poseRef.current.send({ image: videoRef.current });
          }
        },
        width: 640,
        height: 480
      });
      cameraRef.current = camera;
      console.log('Camera initialized');
    }
  };

  const onPoseResults = (results) => {
    if (!canvasRef.current) return;
    if (!results.poseLandmarks) return;

    drawPose(results.poseLandmarks);
    
    // Always process exercise when we have landmarks
    if (currentTest) {
      processExercise(results.poseLandmarks);
    }
  };

  const drawPose = (landmarks) => {
    if (!canvasRef.current) return;
    
    const canvasCtx = canvasRef.current.getContext('2d');
    const canvas = canvasRef.current;

    canvasCtx.save();
    canvasCtx.clearRect(0, 0, canvas.width, canvas.height);

    // Filter out face landmarks (0-10 are face/head)
    const bodyLandmarks = landmarks.map((landmark, index) => {
      if (index <= 10) {
        return { ...landmark, visibility: 0 };
      }
      return landmark;
    });

    drawConnectors(canvasCtx, bodyLandmarks, POSE_CONNECTIONS, {
      color: '#00FF00',
      lineWidth: 4
    });
    drawLandmarks(canvasCtx, bodyLandmarks.filter((_, i) => i > 10), {
      color: '#FF0000',
      lineWidth: 2,
      radius: 6
    });

    canvasCtx.restore();
  };

  const processExercise = (landmarks) => {
    if (!currentTest) return;

    switch (currentTest.id) {
      case 'squat':
        processSquat(landmarks);
        break;
      case 'lunge':
        processLunge(landmarks);
        break;
      case 'tpose':
        processTpose(landmarks);
        break;
      case 'highknee':
        processHighKnee(landmarks);
        break;
      case 'plank':
        processPlank(landmarks);
        break;
      case 'pushup':
        processPushup(landmarks);
        break;
      case 'forwardbend':
        processForwardBend(landmarks);
        break;
      default:
        break;
    }
  };

  const calculateAngle = (a, b, c) => {
    const radians = Math.atan2(c.y - b.y, c.x - b.x) - Math.atan2(a.y - b.y, a.x - b.x);
    let angle = Math.abs(radians * 180.0 / Math.PI);
    if (angle > 180.0) angle = 360 - angle;
    return angle;
  };

  const processSquat = (landmarks) => {
    const leftHip = landmarks[23];
    const leftKnee = landmarks[25];
    const leftAnkle = landmarks[27];
    const rightHip = landmarks[24];
    const rightKnee = landmarks[26];
    const rightAnkle = landmarks[28];

    if (!leftHip || !leftKnee || !leftAnkle) {
      console.log('Missing landmarks for squat');
      return;
    }

    // Calculate knee angle (average of both legs)
    const leftKneeAngle = calculateAngle(leftHip, leftKnee, leftAnkle);
    const rightKneeAngle = rightKnee && rightAnkle ? calculateAngle(rightHip, rightKnee, rightAnkle) : leftKneeAngle;
    const kneeAngle = (leftKneeAngle + rightKneeAngle) / 2;
    
    // Calculate hip height (lower y = higher position)
    const hipHeight = Math.round((1 - leftHip.y) * 100);
    
    const roundedKneeAngle = Math.round(kneeAngle);
    
    setMetrics(prev => ({ 
      ...prev, 
      kneeAngle: roundedKneeAngle,
      hipHeight: hipHeight
    }));

    // Rep counting - using ref for immediate state
    if (kneeAngle < 120 && repStateRef.current === 'up') {
      console.log('Squat DOWN detected, knee angle:', kneeAngle);
      repStateRef.current = 'down';
    } else if (kneeAngle > 160 && repStateRef.current === 'down') {
      console.log('Squat UP detected, knee angle:', kneeAngle, '- COUNTING REP!');
      repStateRef.current = 'up';
      
      setMetrics(prev => {
        const newReps = (prev.reps || 0) + 1;
        console.log('New reps count:', newReps);
        
        if (newReps >= currentTest.target) {
          setTimeout(() => handleTestComplete(), 500);
        }
        
        return { ...prev, reps: newReps };
      });
    }
  };

  const processLunge = (landmarks) => {
    const leftHip = landmarks[23];
    const leftKnee = landmarks[25];
    const leftAnkle = landmarks[27];
    const rightHip = landmarks[24];
    const rightKnee = landmarks[26];
    const rightAnkle = landmarks[28];

    if (!leftHip || !leftKnee || !leftAnkle || !rightHip || !rightKnee || !rightAnkle) {
      console.log('Missing landmarks for lunge');
      return;
    }

    // Calculate both knee angles
    const leftKneeAngle = calculateAngle(leftHip, leftKnee, leftAnkle);
    const rightKneeAngle = calculateAngle(rightHip, rightKnee, rightAnkle);
    
    // Determine which leg is in front (lower knee angle = bent = front leg)
    const frontKneeAngle = Math.min(leftKneeAngle, rightKneeAngle);
    const backKneeAngle = Math.max(leftKneeAngle, rightKneeAngle);

    setMetrics(prev => ({ 
      ...prev, 
      frontKneeAngle: Math.round(frontKneeAngle),
      backKneeAngle: Math.round(backKneeAngle)
    }));

    // Rep counting - front knee bends below 110° and comes back up
    if (frontKneeAngle < 110 && repStateRef.current === 'up') {
      console.log('Lunge DOWN detected, front knee angle:', frontKneeAngle);
      repStateRef.current = 'down';
    } else if (frontKneeAngle > 150 && repStateRef.current === 'down') {
      console.log('Lunge UP detected, front knee angle:', frontKneeAngle, '- COUNTING REP!');
      repStateRef.current = 'up';
      
      setMetrics(prev => {
        const newReps = (prev.reps || 0) + 1;
        console.log('New lunge reps count:', newReps);
        
        if (newReps >= currentTest.target) {
          setTimeout(() => handleTestComplete(), 500);
        }
        
        return { ...prev, reps: newReps };
      });
    }
  };

  const processTpose = (landmarks) => {
    const leftShoulder = landmarks[11];
    const leftElbow = landmarks[13];
    const leftWrist = landmarks[15];
    const rightShoulder = landmarks[12];
    const rightElbow = landmarks[14];
    const rightWrist = landmarks[16];
    const leftAnkle = landmarks[27];
    const rightAnkle = landmarks[28];
    const leftKnee = landmarks[25];
    const rightKnee = landmarks[26];

    if (!leftShoulder || !leftElbow || !leftWrist || !rightShoulder || !rightElbow || !rightWrist) return;

    // Calculate arm angles (should be ~180° for straight arms)
    const leftArmAngle = calculateAngle(leftShoulder, leftElbow, leftWrist);
    const rightArmAngle = calculateAngle(rightShoulder, rightElbow, rightWrist);
    const avgArmAngle = Math.round((leftArmAngle + rightArmAngle) / 2);
    
    // Calculate shoulder alignment (arms should be horizontal)
    const leftArmHorizontal = Math.abs(leftWrist.y - leftShoulder.y) < 0.1;
    const rightArmHorizontal = Math.abs(rightWrist.y - rightShoulder.y) < 0.1;
    const alignment = (leftArmHorizontal && rightArmHorizontal) ? 'Good ✓' : 'Adjust';
    
    // Balance detection: Check if one leg is lifted
    // Compare ankle heights - if one is significantly higher, it's lifted
    let legTouches = metrics.legTouches || 0;
    let liftedLeg = metrics.liftedLeg || null;
    
    if (leftAnkle && rightAnkle && leftKnee && rightKnee) {
      const leftAnkleHeight = 1 - leftAnkle.y; // Higher y = lower on screen
      const rightAnkleHeight = 1 - rightAnkle.y;
      const heightDiff = Math.abs(leftAnkleHeight - rightAnkleHeight);
      
      // If one ankle is significantly higher (>0.15), that leg is lifted
      if (heightDiff > 0.15) {
        const currentLiftedLeg = leftAnkleHeight > rightAnkleHeight ? 'left' : 'right';
        
        // If this is the first time detecting a lifted leg, set it
        if (!liftedLeg) {
          liftedLeg = currentLiftedLeg;
        }
        
        // Track if the lifted leg touches down
        const liftedAnkleHeight = liftedLeg === 'left' ? leftAnkleHeight : rightAnkleHeight;
        const standingAnkleHeight = liftedLeg === 'left' ? rightAnkleHeight : leftAnkleHeight;
        
        // If lifted leg comes close to standing leg height, it touched
        if (Math.abs(liftedAnkleHeight - standingAnkleHeight) < 0.05 && repStateRef.current === 'up') {
          legTouches++;
          repStateRef.current = 'down';
          console.log('Leg touch detected! Total touches:', legTouches);
        } else if (Math.abs(liftedAnkleHeight - standingAnkleHeight) > 0.1) {
          repStateRef.current = 'up'; // Reset for next touch
        }
      }
    }
    
    setMetrics(prev => ({ 
      ...prev, 
      armAngle: avgArmAngle,
      shoulderAlignment: alignment,
      legTouches: legTouches,
      liftedLeg: liftedLeg
    }));
    
    // T-Pose is time-based, auto-completes after 20 seconds (handled by startTimeBasedTest)
  };

  const processHighKnee = (landmarks) => {
    const leftHip = landmarks[23];
    const leftKnee = landmarks[25];
    const rightHip = landmarks[24];
    const rightKnee = landmarks[26];
    const leftAnkle = landmarks[27];
    const rightAnkle = landmarks[28];

    if (!leftHip || !leftKnee || !rightHip || !rightKnee || !leftAnkle || !rightAnkle) {
      console.log('Missing landmarks for high knee');
      return;
    }

    // Calculate knee heights - knee above hip means raised
    // When knee.y < hip.y, knee is higher (lower y = higher on screen)
    const leftKneeHeight = Math.max(0, (leftHip.y - leftKnee.y) * 100);
    const rightKneeHeight = Math.max(0, (rightHip.y - rightKnee.y) * 100);
    
    // Track which knee is currently raised higher
    const maxKneeHeight = Math.max(leftKneeHeight, rightKneeHeight);
    const currentRaisedLeg = leftKneeHeight > rightKneeHeight ? 'left' : 'right';
    
    // Calculate hip angles for both legs
    const leftHipAngle = calculateAngle(landmarks[11], leftHip, leftKnee);
    const rightHipAngle = calculateAngle(landmarks[12], rightHip, rightKnee);
    const avgHipAngle = Math.round((leftHipAngle + rightHipAngle) / 2);
    
    console.log('High knee march:', { 
      leftHeight: Math.round(leftKneeHeight), 
      rightHeight: Math.round(rightKneeHeight), 
      max: Math.round(maxKneeHeight),
      raisedLeg: currentRaisedLeg,
      lastLeg: metrics.lastRaisedLeg
    });
    
    setMetrics(prev => ({ 
      ...prev, 
      kneeHeight: Math.round(maxKneeHeight),
      hipAngle: avgHipAngle,
      currentRaisedLeg: currentRaisedLeg
    }));

    // Count alternating leg raises
    // Knee must be raised (knee.y < hip.y means knee is above hip)
    const kneeIsRaised = maxKneeHeight > 5; // Lower threshold for detection
    
    if (kneeIsRaised && repStateRef.current === 'down') {
      const lastRaisedLeg = metrics.lastRaisedLeg;
      const leftLegDone = metrics.leftLegDone || false;
      const rightLegDone = metrics.rightLegDone || false;
      
      console.log('High knee march - leg raised:', currentRaisedLeg, 'height:', maxKneeHeight);
      repStateRef.current = 'up';
      
      setMetrics(prev => {
        let newLeftDone = prev.leftLegDone || false;
        let newRightDone = prev.rightLegDone || false;
        let newReps = prev.reps || 0;
        
        // Mark which leg was raised
        if (currentRaisedLeg === 'left') {
          newLeftDone = true;
        } else {
          newRightDone = true;
        }
        
        // If both legs have been raised, count 1 rep and reset
        if (newLeftDone && newRightDone) {
          newReps = newReps + 1;
          newLeftDone = false;
          newRightDone = false;
          console.log('✓ COMPLETE SET! Both legs raised. Rep count:', newReps);
          
          // Target is 10 reps
          if (newReps >= currentTest.target) {
            setTimeout(() => handleTestComplete(), 500);
          }
        }
        
        return { 
          ...prev, 
          reps: newReps,
          leftLegDone: newLeftDone,
          rightLegDone: newRightDone,
          lastRaisedLeg: currentRaisedLeg
        };
      });
    } else if (maxKneeHeight < 3) {
      repStateRef.current = 'down';
    }
  };

  const processPlank = (landmarks) => {
    const leftShoulder = landmarks[11];
    const rightShoulder = landmarks[12];
    const leftHip = landmarks[23];
    const rightHip = landmarks[24];
    const leftAnkle = landmarks[27];
    const rightAnkle = landmarks[28];
    const leftElbow = landmarks[13];
    const rightElbow = landmarks[14];

    if (!leftShoulder || !leftHip || !leftAnkle || !rightShoulder || !rightHip || !rightAnkle) {
      console.log('Missing landmarks for plank');
      return;
    }

    // Calculate back angle (shoulder to hip to ankle) - should be straight (~180°)
    const leftBackAngle = calculateAngle(leftShoulder, leftHip, leftAnkle);
    const rightBackAngle = calculateAngle(rightShoulder, rightHip, rightAnkle);
    const avgBackAngle = Math.round((leftBackAngle + rightBackAngle) / 2);
    
    // Check if back is straight (good plank form)
    const backIsStraight = avgBackAngle > 160 && avgBackAngle < 200;
    
    // Calculate hip alignment - hips should not sag or pike up
    // Compare hip height to shoulder and ankle
    const avgHipY = (leftHip.y + rightHip.y) / 2;
    const avgShoulderY = (leftShoulder.y + rightShoulder.y) / 2;
    const avgAnkleY = (leftAnkle.y + rightAnkle.y) / 2;
    
    // Hip should be roughly in line (between shoulder and ankle)
    const hipAlignment = avgHipY > avgShoulderY && avgHipY < avgAnkleY ? 'Good ✓' : 'Adjust';
    
    console.log('Plank:', {
      backAngle: avgBackAngle,
      backStraight: backIsStraight,
      hipAlignment,
      hipY: avgHipY.toFixed(2),
      shoulderY: avgShoulderY.toFixed(2)
    });
    
    setMetrics(prev => ({ 
      ...prev, 
      backAngle: avgBackAngle,
      backAlignment: backIsStraight ? 'Straight ✓' : 'Adjust',
      hipAlignment: hipAlignment
    }));
    
    // Plank is time-based, auto-completes after 20 seconds (handled by startTimeBasedTest)
  };

  const processPushup = (landmarks) => {
    const leftShoulder = landmarks[11];
    const leftElbow = landmarks[13];
    const leftWrist = landmarks[15];
    const rightShoulder = landmarks[12];
    const rightElbow = landmarks[14];
    const rightWrist = landmarks[16];
    const leftHip = landmarks[23];
    const rightHip = landmarks[24];
    const leftAnkle = landmarks[27];
    const rightAnkle = landmarks[28];

    if (!leftShoulder || !leftElbow || !leftWrist || !rightShoulder || !rightElbow || !rightWrist) {
      console.log('Missing landmarks for pushup');
      return;
    }

    // Calculate elbow angles (average both arms)
    const leftElbowAngle = calculateAngle(leftShoulder, leftElbow, leftWrist);
    const rightElbowAngle = calculateAngle(rightShoulder, rightElbow, rightWrist);
    const avgElbowAngle = Math.round((leftElbowAngle + rightElbowAngle) / 2);
    
    // Calculate body alignment (shoulder to hip to ankle) - should be straight
    let bodyAlignment = 'N/A';
    if (leftHip && leftAnkle) {
      const bodyAngle = calculateAngle(leftShoulder, leftHip, leftAnkle);
      const isAligned = bodyAngle > 160 && bodyAngle < 200;
      bodyAlignment = isAligned ? 'Straight ✓' : 'Adjust';
    }
    
    console.log('Pushup:', {
      elbowAngle: avgElbowAngle,
      bodyAlignment,
      state: repStateRef.current
    });
    
    setMetrics(prev => ({ 
      ...prev, 
      elbowAngle: avgElbowAngle,
      bodyAlignment: bodyAlignment
    }));

    // Rep counting - down (elbow < 90°) and up (elbow > 160°)
    if (avgElbowAngle < 90 && repStateRef.current === 'up') {
      console.log('Pushup DOWN detected, elbow angle:', avgElbowAngle);
      repStateRef.current = 'down';
    } else if (avgElbowAngle > 160 && repStateRef.current === 'down') {
      console.log('Pushup UP detected, elbow angle:', avgElbowAngle, '- COUNTING REP!');
      repStateRef.current = 'up';
      
      setMetrics(prev => {
        const newReps = (prev.reps || 0) + 1;
        console.log('New pushup reps count:', newReps);
        
        if (newReps >= currentTest.target) {
          setTimeout(() => handleTestComplete(), 500);
        }
        
        return { ...prev, reps: newReps };
      });
    }
  };

  const processForwardBend = (landmarks) => {
    const leftShoulder = landmarks[11];
    const rightShoulder = landmarks[12];
    const leftHip = landmarks[23];
    const rightHip = landmarks[24];
    const leftKnee = landmarks[25];
    const rightKnee = landmarks[26];
    const leftAnkle = landmarks[27];
    const rightAnkle = landmarks[28];
    const leftWrist = landmarks[15];
    const rightWrist = landmarks[16];

    if (!leftShoulder || !leftHip || !leftKnee || !leftAnkle || !rightHip || !rightKnee || !rightAnkle) {
      console.log('Missing landmarks for forward bend');
      return;
    }

    // Calculate torso angle (shoulder to hip to knee) - measures forward bend
    const torsoAngle = calculateAngle(leftShoulder, leftHip, leftKnee);
    
    // Calculate knee angles - should be straight (close to 180°)
    const leftKneeAngle = calculateAngle(leftHip, leftKnee, leftAnkle);
    const rightKneeAngle = calculateAngle(rightHip, rightKnee, rightAnkle);
    const avgKneeAngle = Math.round((leftKneeAngle + rightKneeAngle) / 2);
    
    // Check if knees are straight (good form)
    const kneesAreStraight = avgKneeAngle > 160;
    
    // Calculate how close hands are to feet (lower y = higher on screen)
    const avgWristY = ((leftWrist?.y || 0) + (rightWrist?.y || 0)) / 2;
    const avgAnkleY = ((leftAnkle?.y || 0) + (rightAnkle?.y || 0)) / 2;
    const handToFootDistance = Math.abs(avgWristY - avgAnkleY) * 100;
    const touchingToes = handToFootDistance < 20; // Close to toes
    
    console.log('Forward bend:', {
      torsoAngle: Math.round(torsoAngle),
      kneeAngle: avgKneeAngle,
      kneesAreStraight,
      handToFootDist: Math.round(handToFootDistance),
      touchingToes
    });
    
    setMetrics(prev => ({ 
      ...prev, 
      torsoAngle: Math.round(torsoAngle),
      kneeAngle: avgKneeAngle,
      kneesAreStraight: kneesAreStraight ? 'Yes ✓' : 'Straighten',
      reachDistance: Math.round(handToFootDistance)
    }));

    // Rep counting - bend down (torso angle < 90) and come back up (> 140)
    // Bonus points for straight knees
    if (torsoAngle < 90 && touchingToes && repStateRef.current === 'up') {
      console.log('Forward bend DOWN - touching toes, torso angle:', torsoAngle);
      repStateRef.current = 'down';
    } else if (torsoAngle > 140 && repStateRef.current === 'down') {
      console.log('Forward bend UP - standing, torso angle:', torsoAngle, '- COUNTING REP!');
      repStateRef.current = 'up';
      
      setMetrics(prev => {
        const newReps = (prev.reps || 0) + 1;
        console.log('New forward bend reps count:', newReps);
        
        if (newReps >= currentTest.target) {
          setTimeout(() => handleTestComplete(), 500);
        }
        
        return { ...prev, reps: newReps };
      });
    }
  };

  const handleTestComplete = async () => {
    // Stop tracking first to prevent further processing
    setIsTracking(false);
    
    // Stop all timers
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Stop camera
    if (cameraRef.current) {
      try {
        cameraRef.current.stop();
      } catch (error) {
        console.error('Error stopping camera:', error);
      }
      cameraRef.current = null;
    }
    
    // Clear pose ref
    poseRef.current = null;

    // Save test result with elapsed time
    const result = {
      testId: currentTest.id,
      testName: currentTest.name,
      ...metrics,
      duration: metrics.elapsedTime || 0,
      completedAt: new Date().toISOString()
    };

    await savePhysicalTest(currentTest.id, result);
    setCompletedTests([...completedTests, result]);
    
    // Speak completion
    if (completedTests.length + 1 >= 4) {
      speakInstruction(audioInstructions.allComplete);
    } else {
      speakInstruction(audioInstructions.complete);
    }
    
    // Clear current test last
    setCurrentTest(null);
    setShowInstructions(false);
  };

  const handleProceedToReports = async () => {
    await completeAssessment();
    navigate(`/report/${currentAssessment?.id}`);
  };

  // Render test selection screen
  if (!currentTest && !showInstructions) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4">
        <div className="max-w-7xl mx-auto py-8">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">🏃</div>
            <h1 className="text-3xl font-bold text-slate-100 mb-2">
              Physical Fitness Tests
            </h1>
            <p className="text-slate-400 mb-6">
              Complete 4 out of 7 tests to measure your physical capabilities
            </p>
            <ProgressBar
              current={completedTests.length}
              total={4}
              showLabel={true}
              showPercentage={true}
              size="lg"
              color="secondary"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {availableTests.map(test => {
              const isCompleted = completedTests.some(t => t.testId === test.id);
              
              return (
                <Card
                  key={test.id}
                  className={`relative ${isCompleted ? 'border-green-500' : ''}`}
                  hover={!isCompleted}
                >
                  {isCompleted && (
                    <div className="absolute top-4 right-4">
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  )}

                  <div className="text-center">
                    <div className="text-5xl mb-3">{test.icon}</div>
                    <h3 className="text-xl font-bold text-slate-100 mb-2">{test.name}</h3>
                    <p className="text-slate-400 text-sm mb-4">{test.description}</p>
                    <p className="text-slate-500 text-sm mb-4">
                      Target: {test.target} {test.type === 'reps' ? 'reps' : 'seconds'}
                    </p>
                    
                    <Button
                      variant={isCompleted ? 'outline' : 'primary'}
                      fullWidth
                      onClick={() => handleSelectTest(test)}
                      disabled={isCompleted}
                    >
                      {isCompleted ? 'Completed ✓' : 'Start Test'}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>

          {completedTests.length >= 4 && (
            <div className="flex justify-center">
              <Button
                variant="primary"
                size="lg"
                onClick={handleProceedToReports}
                className="min-w-64"
              >
                Proceed to Reports →
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render instructions screen
  if (showInstructions) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-2xl">
          <div className="text-center">
            <div className="text-8xl mb-6">{currentTest.icon}</div>
            <h2 className="text-3xl font-bold text-slate-100 mb-4">{currentTest.name}</h2>
            <p className="text-slate-400 text-lg mb-6">
              Complete {currentTest.target} {currentTest.type === 'reps' ? 'repetitions' : 'seconds'}
            </p>
            
            <div className="bg-slate-700/50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-slate-200 mb-3">Instructions:</h3>
              <ul className="text-slate-400 text-left space-y-2">
                <li>• Position yourself so your full body is visible</li>
                <li>• Maintain proper form throughout</li>
                <li>• The system will count automatically</li>
                <li>• Test stops after reaching target</li>
              </ul>
            </div>

            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => setCurrentTest(null)}>
                Cancel
              </Button>
              <Button variant="primary" size="lg" onClick={handleStartTest}>
                Start Test
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Render camera/tracking screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4">
      <div className="max-w-7xl mx-auto py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Camera */}
          <div className="lg:col-span-2">
            <Card padding="none">
              <div className="relative bg-black rounded-xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
                <video
                  ref={videoRef}
                  className="absolute inset-0 w-full h-full object-cover transform scale-x-[-1]"
                  autoPlay
                  playsInline
                  muted
                />
                <canvas
                  ref={canvasRef}
                  width={640}
                  height={480}
                  className="absolute inset-0 w-full h-full object-cover transform scale-x-[-1]"
                />
                
                <div className="absolute top-4 left-4 bg-slate-900/80 backdrop-blur-sm rounded-lg p-3">
                  <p className="text-slate-100 font-semibold">{currentTest.name}</p>
                  <p className="text-slate-400 text-sm">🟢 Tracking Active</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Metrics */}
          <div className="space-y-4">
            <Card className="bg-gradient-to-br from-primary-600 to-primary-700 border-primary-500">
              <div className="text-center">
                <p className="text-primary-100 text-sm mb-2">
                  {currentTest.type === 'reps' ? 'Reps' : 'Time'}
                </p>
                <div className="text-7xl font-bold text-white mb-2">
                  {currentTest.type === 'reps' ? metrics.reps || 0 : metrics.time || 0}
                </div>
                <p className="text-primary-100 text-sm">
                  / {currentTest.target} {currentTest.type === 'reps' ? 'reps' : 'sec'}
                </p>
              </div>
            </Card>

            {/* Exercise-specific metrics */}
            {renderExerciseMetrics()}
          </div>
        </div>
      </div>
    </div>
  );

  function renderExerciseMetrics() {
    if (!currentTest) return null;

    const metricCards = [];

    // Timer card - always show for all tests
    if (metrics.elapsedTime !== undefined) {
      metricCards.push(
        <Card key="timer" className="bg-gradient-to-br from-slate-600 to-slate-700 border-slate-500">
          <div className="text-center">
            <p className="text-slate-100 text-sm mb-2">⏱️ Time Elapsed</p>
            <div className="text-5xl font-bold text-white">{metrics.elapsedTime}s</div>
          </div>
        </Card>
      );
    }

    // Add relevant metric cards based on exercise
    if (metrics.kneeAngle !== undefined) {
      metricCards.push(
        <Card key="knee" className="bg-gradient-to-br from-blue-600 to-blue-700 border-blue-500">
          <div className="text-center">
            <p className="text-blue-100 text-sm mb-2">Knee Angle</p>
            <div className="text-5xl font-bold text-white">{metrics.kneeAngle}°</div>
          </div>
        </Card>
      );
    }

    if (metrics.hipHeight !== undefined) {
      metricCards.push(
        <Card key="hip" className="bg-gradient-to-br from-purple-600 to-purple-700 border-purple-500">
          <div className="text-center">
            <p className="text-purple-100 text-sm mb-2">Hip Height</p>
            <div className="text-4xl font-bold text-white">{metrics.hipHeight}%</div>
          </div>
        </Card>
      );
    }

    if (metrics.elbowAngle !== undefined) {
      metricCards.push(
        <Card key="elbow" className="bg-gradient-to-br from-green-600 to-green-700 border-green-500">
          <div className="text-center">
            <p className="text-green-100 text-sm mb-2">Elbow Angle</p>
            <div className="text-5xl font-bold text-white">{metrics.elbowAngle}°</div>
            <p className="text-green-200 text-xs mt-2">Down: &lt;90° | Up: &gt;160°</p>
          </div>
        </Card>
      );
    }

    if (metrics.bodyAlignment !== undefined && currentTest?.id === 'pushup') {
      const bodyAlignStr = String(metrics.bodyAlignment);
      const isAligned = bodyAlignStr.includes('✓') || bodyAlignStr === 'Straight';
      metricCards.push(
        <Card key="bodyalign" className={`bg-gradient-to-br ${isAligned ? 'from-emerald-600 to-emerald-700 border-emerald-500' : 'from-rose-600 to-rose-700 border-rose-500'}`}>
          <div className="text-center">
            <p className={`${isAligned ? 'text-emerald-100' : 'text-rose-100'} text-sm mb-2`}>Body</p>
            <div className="text-4xl font-bold text-white">{bodyAlignStr}</div>
            <p className={`${isAligned ? 'text-emerald-200' : 'text-rose-200'} text-xs mt-2`}>
              {isAligned ? 'Perfect form!' : 'Keep body straight'}
            </p>
          </div>
        </Card>
      );
    }

    if (metrics.frontKneeAngle !== undefined) {
      metricCards.push(
        <Card key="frontknee" className="bg-gradient-to-br from-blue-600 to-blue-700 border-blue-500">
          <div className="text-center">
            <p className="text-blue-100 text-sm mb-2">Front Knee</p>
            <div className="text-5xl font-bold text-white">{metrics.frontKneeAngle}°</div>
          </div>
        </Card>
      );
    }

    if (metrics.backKneeAngle !== undefined) {
      metricCards.push(
        <Card key="backknee" className="bg-gradient-to-br from-indigo-600 to-indigo-700 border-indigo-500">
          <div className="text-center">
            <p className="text-indigo-100 text-sm mb-2">Back Knee</p>
            <div className="text-5xl font-bold text-white">{metrics.backKneeAngle}°</div>
          </div>
        </Card>
      );
    }

    if (metrics.armAngle !== undefined) {
      metricCards.push(
        <Card key="arm" className="bg-gradient-to-br from-yellow-600 to-yellow-700 border-yellow-500">
          <div className="text-center">
            <p className="text-yellow-100 text-sm mb-2">Arm Angle</p>
            <div className="text-5xl font-bold text-white">{metrics.armAngle}°</div>
            <p className="text-yellow-200 text-xs mt-2">Target: ~180° (straight)</p>
          </div>
        </Card>
      );
    }

    if (metrics.shoulderAlignment !== undefined && metrics.shoulderAlignment !== null) {
      const alignmentStr = String(metrics.shoulderAlignment);
      const isGood = alignmentStr.includes('✓') || alignmentStr === 'Good';
      metricCards.push(
        <Card key="alignment" className={`bg-gradient-to-br ${isGood ? 'from-green-600 to-green-700 border-green-500' : 'from-orange-600 to-orange-700 border-orange-500'}`}>
          <div className="text-center">
            <p className={`${isGood ? 'text-green-100' : 'text-orange-100'} text-sm mb-2`}>Alignment</p>
            <div className="text-4xl font-bold text-white">{alignmentStr}</div>
            <p className={`${isGood ? 'text-green-200' : 'text-orange-200'} text-xs mt-2`}>
              {isGood ? 'Arms horizontal' : 'Raise arms to shoulder level'}
            </p>
          </div>
        </Card>
      );
    }

    if (metrics.legTouches !== undefined) {
      metricCards.push(
        <Card key="legtouches" className="bg-gradient-to-br from-indigo-600 to-indigo-700 border-indigo-500">
          <div className="text-center">
            <p className="text-indigo-100 text-sm mb-2">Leg Touches</p>
            <div className="text-6xl font-bold text-white">{metrics.legTouches}</div>
            <p className="text-indigo-200 text-xs mt-2">
              {metrics.legTouches === 0 ? '🏆 Perfect balance!' : 'Lower = better balance'}
            </p>
          </div>
        </Card>
      );
    }

    if (metrics.kneeHeight !== undefined) {
      const isGoodHeight = metrics.kneeHeight > 5; // Lower threshold - just needs to be raised
      metricCards.push(
        <Card key="kneeheight" className={`bg-gradient-to-br ${isGoodHeight ? 'from-pink-600 to-pink-700 border-pink-500' : 'from-gray-600 to-gray-700 border-gray-500'}`}>
          <div className="text-center">
            <p className={`${isGoodHeight ? 'text-pink-100' : 'text-gray-100'} text-sm mb-2`}>Knee Height</p>
            <div className="text-4xl font-bold text-white">{metrics.kneeHeight}%</div>
            <p className={`${isGoodHeight ? 'text-pink-200' : 'text-gray-200'} text-xs mt-2`}>
              {isGoodHeight ? '✓ Knee raised' : 'Raise knee'}
            </p>
          </div>
        </Card>
      );
    }

    if (metrics.backAngle !== undefined) {
      metricCards.push(
        <Card key="back" className="bg-gradient-to-br from-orange-600 to-orange-700 border-orange-500">
          <div className="text-center">
            <p className="text-orange-100 text-sm mb-2">Back Angle</p>
            <div className="text-5xl font-bold text-white">{metrics.backAngle}°</div>
            <p className="text-orange-200 text-xs mt-2">Target: ~180° (straight)</p>
          </div>
        </Card>
      );
    }

    if (metrics.backAlignment !== undefined) {
      const isStraight = metrics.backAlignment.includes('✓');
      metricCards.push(
        <Card key="backalign" className={`bg-gradient-to-br ${isStraight ? 'from-green-600 to-green-700 border-green-500' : 'from-red-600 to-red-700 border-red-500'}`}>
          <div className="text-center">
            <p className={`${isStraight ? 'text-green-100' : 'text-red-100'} text-sm mb-2`}>Back</p>
            <div className="text-4xl font-bold text-white">{metrics.backAlignment}</div>
            <p className={`${isStraight ? 'text-green-200' : 'text-red-200'} text-xs mt-2`}>
              {isStraight ? 'Perfect form!' : 'Keep back straight'}
            </p>
          </div>
        </Card>
      );
    }

    if (metrics.hipAlignment !== undefined && currentTest?.id === 'plank') {
      const hipAlignStr = String(metrics.hipAlignment);
      const isGood = hipAlignStr.includes('✓') || hipAlignStr === 'Good';
      metricCards.push(
        <Card key="hipalign" className={`bg-gradient-to-br ${isGood ? 'from-blue-600 to-blue-700 border-blue-500' : 'from-amber-600 to-amber-700 border-amber-500'}`}>
          <div className="text-center">
            <p className={`${isGood ? 'text-blue-100' : 'text-amber-100'} text-sm mb-2`}>Hip Position</p>
            <div className="text-4xl font-bold text-white">{hipAlignStr}</div>
            <p className={`${isGood ? 'text-blue-200' : 'text-amber-200'} text-xs mt-2`}>
              {isGood ? 'Level hips!' : 'Don\'t sag or pike'}
            </p>
          </div>
        </Card>
      );
    }

    if (metrics.hipAngle !== undefined) {
      metricCards.push(
        <Card key="hipangle" className="bg-gradient-to-br from-teal-600 to-teal-700 border-teal-500">
          <div className="text-center">
            <p className="text-teal-100 text-sm mb-2">Hip Angle</p>
            <div className="text-5xl font-bold text-white">{metrics.hipAngle}°</div>
          </div>
        </Card>
      );
    }

    if (metrics.torsoAngle !== undefined) {
      metricCards.push(
        <Card key="torso" className="bg-gradient-to-br from-purple-600 to-purple-700 border-purple-500">
          <div className="text-center">
            <p className="text-purple-100 text-sm mb-2">Torso Angle</p>
            <div className="text-5xl font-bold text-white">{metrics.torsoAngle}°</div>
            <p className="text-purple-200 text-xs mt-2">Lower = deeper bend</p>
          </div>
        </Card>
      );
    }

    if (metrics.kneesAreStraight !== undefined) {
      const isStraight = metrics.kneesAreStraight.includes('✓');
      metricCards.push(
        <Card key="kneestraight" className={`bg-gradient-to-br ${isStraight ? 'from-green-600 to-green-700 border-green-500' : 'from-yellow-600 to-yellow-700 border-yellow-500'}`}>
          <div className="text-center">
            <p className={`${isStraight ? 'text-green-100' : 'text-yellow-100'} text-sm mb-2`}>Knees</p>
            <div className="text-4xl font-bold text-white">{metrics.kneesAreStraight}</div>
            <p className={`${isStraight ? 'text-green-200' : 'text-yellow-200'} text-xs mt-2`}>
              {isStraight ? 'Perfect form!' : 'Keep knees straight'}
            </p>
          </div>
        </Card>
      );
    }

    if (metrics.reachDistance !== undefined) {
      const touching = metrics.reachDistance < 20;
      metricCards.push(
        <Card key="reach" className={`bg-gradient-to-br ${touching ? 'from-cyan-600 to-cyan-700 border-cyan-500' : 'from-slate-600 to-slate-700 border-slate-500'}`}>
          <div className="text-center">
            <p className={`${touching ? 'text-cyan-100' : 'text-slate-100'} text-sm mb-2`}>Reach</p>
            <div className="text-4xl font-bold text-white">{metrics.reachDistance}%</div>
            <p className={`${touching ? 'text-cyan-200' : 'text-slate-200'} text-xs mt-2`}>
              {touching ? '✓ Touching toes!' : 'Reach for toes'}
            </p>
          </div>
        </Card>
      );
    }

    return metricCards;
  }
};

export default PhysicalTestsModule;
